import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  #pendingLocalOptionRefetch = false;

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.checked);
      this.#checkedIndices.push(initialCheckedIndex !== -1 ? [initialCheckedIndex] : []);
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    // A non-existent combination's connected-product-url can degrade to the bare listing parent. When we have a
    // substitute, prefer its own product url so we load the correct child (right gallery + coherent options)
    // instead of the parent's default variant.
    const newUrl = selectedOption.dataset.substituteProductUrl || selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    // Fallback for a cross-product (combined listing) non-existent combination when Liquid couldn't precompute a
    // substitute (no data-substitute-variant-id, e.g. all_products returned blank). In that case the substitute is
    // only discoverable from the destination product's response, so ask fetchUpdatedSection to resolve it there.
    const needsSubstituteResolution =
      loadsNewProduct && !selectedOption.dataset.variantId && !selectedOption.dataset.substituteVariantId;

    this.fetchUpdatedSection(
      this.buildRequestUrl(selectedOption),
      morphElementSelector,
      needsSubstituteResolution ? selectedOption.dataset.optionValueId : undefined
    );

    const url = new URL(window.location.href);

    // Reflect the resolved variant in the URL: the directly-selected variant, or its substitute for a non-existent
    // combination. When neither is known (cross-product resolution happens after the fetch), fetchUpdatedSection
    // updates the URL once it lands on the substitute.
    const variantId = selectedOption.dataset.variantId || selectedOption.dataset.substituteVariantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    // When the selected option's combination doesn't exist on the current product (no resolved variant),
    // redirect to a substitute instead of requesting the missing combination (which falls back to the first
    // variant). A resolvable option always carries a data-variant-id.
    const { substituteVariantId, substituteProductUrl } = selectedOption.dataset;
    const isUnavailable = !selectedOption.dataset.variantId;
    const currentBase = (this.#pendingRequestUrl || this.dataset.productUrl)?.split('?')[0];
    const connectedBase = selectedOption.dataset.connectedProductUrl?.split('?')[0];
    const isSibling = Boolean(connectedBase) && connectedBase !== currentBase;

    let productUrl;

    if (isUnavailable && substituteVariantId) {
      // Non-existent combination with a known substitute. Load the substitute variant's OWN product (a combined-
      // listing value's connected url can degrade to the bare listing parent, which would render the parent's
      // default variant + gallery); fall back to the connected/current url only if it wasn't resolved in Liquid.
      productUrl = substituteProductUrl || (isSibling ? connectedBase : currentBase);
      this.#pendingRequestUrl = productUrl;
      params.push(`variant=${substituteVariantId}`);
    } else if (isUnavailable && isSibling) {
      // Non-existent combination linked to another product (combined listing). The connected URL can degrade to
      // the listing's parent product, so request only this option's value so the destination at least selects the
      // right value (e.g. the colour). If that still lands on an unavailable combination, fetchUpdatedSection's
      // substitute resolution redirects to the first available variant for the value.
      productUrl = connectedBase;
      this.#pendingRequestUrl = productUrl;
      params.push(`option_values=${selectedOption.dataset.optionValueId}`);
    } else {
      // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
      // We create a new URL and abort the previous fetch request if it's still pending.
      productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
      this.#pendingRequestUrl = productUrl;

      if (selectedOption.dataset.variantId && !isSibling && !source) {
        // For local options on combined listing products (e.g. Canopy Span with no connected product URL),
        // use variant= directly so the server returns the exact variant the user selected.
        // option_values= is unreliable for these options and causes the server to fall back to first_available_variant.
        params.push(`variant=${selectedOption.dataset.variantId}`);
      } else if (this.selectedOptionsValues.length && !source) {
        if (isSibling) {
          // Switching a sibling option (e.g. Color) that loads a different product. Local option IDs
          // (e.g. Canopy Span) belong to the CURRENT product and are not valid on the target product.
          // Only send sibling option IDs so the server resolves correctly. After morph, updateMain will
          // re-fetch the correct local variant if the user had a non-default selection.
          const siblingIds = Array.from(this.querySelectorAll('fieldset input:checked'))
            .filter((r) => /** @type {HTMLInputElement} */(r).dataset.connectedProductUrl)
            .map((r) => /** @type {HTMLInputElement} */(r).dataset.optionValueId)
            .filter(Boolean);
          if (siblingIds.length) params.push(`option_values=${siblingIds.join(',')}`);
        } else {
          params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
        }
      } else if (source === 'product-card') {
        if (this.selectedOptionsValues.length) {
          params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
        } else {
          params.push(`option_values=${selectedOption.dataset.optionValueId}`);
        }
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {string} [resolveOptionValueId] - Option value id to resolve to a substitute variant. Fallback path for
   *   cross-product (combined listing) non-existent combinations where Liquid couldn't precompute a substitute: if
   *   the destination response still has no variant for this value but exposes a data-substitute-variant-id, we
   *   transparently re-fetch that available variant instead of rendering the unavailable combination.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, resolveOptionValueId) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        // The destination resolved to an unavailable combination but offers a substitute: redirect to it instead.
        if (resolveOptionValueId && this.#redirectToSubstitute(html, requestUrl, morphElementSelector, resolveOptionValueId)) {
          return;
        }

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (this.#pendingLocalOptionRefetch) {
          this.#pendingLocalOptionRefetch = false;
          const buyButtonsBlock = document.querySelector('.buy-buttons-block');
          if (buyButtonsBlock instanceof HTMLElement) buyButtonsBlock.style.visibility = '';
        }
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Handles a destination that resolved to an unavailable combination for the selected value. The destination
   * provides a substitute (first available) variant id for that value, so we re-fetch that variant — landing the
   * shopper on the first available variant for their selection (e.g. the chosen colour) rather than the
   * destination's unavailable default.
   * @param {Document} html - The fetched destination HTML.
   * @param {string} requestUrl - The URL that produced `html`.
   * @param {string} [morphElementSelector]
   * @param {string} [optionValueId] - The selected option value id to resolve.
   * @returns {boolean} True if a substitute re-fetch was triggered (the caller should stop processing `html`).
   */
  #redirectToSubstitute(html, requestUrl, morphElementSelector, optionValueId) {
    const target = html.querySelector(`variant-picker [data-option-value-id="${optionValueId}"]`);
    if (!(target instanceof HTMLElement)) return false;

    const { substituteVariantId, substituteProductUrl } = target.dataset;
    // Only redirect when the destination still has no variant for this value (the combination is unavailable).
    if (!substituteVariantId || target.dataset.variantId) return false;

    const [requestBase = requestUrl, query] = requestUrl.split('?');
    // Prefer the substitute variant's own product url: the first request may have hit a degraded listing parent,
    // and re-fetching that parent path would render the parent's default variant + gallery instead of the child.
    const base = substituteProductUrl || requestBase;
    const sourceParams = new URLSearchParams(query);
    const params = new URLSearchParams();
    const sectionId = sourceParams.get('section_id');
    if (sectionId) params.set('section_id', sectionId);
    const view = sourceParams.get('view');
    if (view) params.set('view', view);
    params.set('variant', substituteVariantId);

    // Reflect the resolved variant in the address bar of the main product page.
    if (!sectionId && this.dataset.templateProductMatch === 'true') {
      const addressUrl = new URL(base, window.location.origin);
      if (view) addressUrl.searchParams.set('view', view);
      addressUrl.searchParams.set('variant', substituteVariantId);
      yieldToMainThread().then(() => history.replaceState({}, '', addressUrl.toString()));
    }

    // Re-fetch without resolveOptionValueId so this resolves at most once (the response now has a variant).
    this.fetchUpdatedSection(`${base}?${params.toString()}`, morphElementSelector);
    return true;
  }

  /**
   * Rebuilds the radio bookkeeping (#radios / #checkedIndices) and resets data-previous-checked after the picker
   * is re-rendered for a different product (combined listing). connectedCallback runs only once, so when the
   * product swaps via a morph the indices captured for the original product go stale — leaving the next option
   * change to mark the wrong (typically first) value as selected. (data-current-checked is handled separately by
   * #syncCheckedState, which runs after every morph.)
   */
  #resyncRadioState() {
    const fieldsets = Array.from(this.querySelectorAll('[ref="fieldsets[]"]'));
    this.#radios = [];
    this.#checkedIndices = [];

    for (const fieldset of fieldsets) {
      const radios = Array.from(fieldset.querySelectorAll('input'));
      this.#radios.push(radios);

      let checkedIndex = -1;
      radios.forEach((radio, index) => {
        radio.dataset.previousChecked = 'false';
        if (radio.checked) checkedIndex = index;
      });

      this.#checkedIndices.push(checkedIndex === -1 ? [] : [checkedIndex]);
    }
  }

  /**
   * Re-derives every option's checked state from the PRISTINE server response after a morph. Reading the live
   * post-morph DOM is unreliable: morph's onBeforeUpdate preserves the old data-current-checked, and a radio the
   * user clicked is left "dirty" (updateSelectedOption set .checked), so its property/attribute survive morph's
   * churn inconsistently. The freshly-parsed server source has exactly one checked radio per group, so we apply
   * that directly — forcing both the .checked property and data-current-checked. Option value ids are stable
   * across a combined listing, so we match by them. data-previous-checked is left untouched so the clicked
   * option's slide animation is preserved.
   * @param {Element | null} [newSource] - The pristine new variant-picker element from the server response.
   * @param {string | null} [preserveLocalValue] - Local option value string to preserve (e.g. "12'") when
   *   cross-product morph replaces all local radios with fresh nodes.
   */
  #syncCheckedState(newSource, preserveLocalValue = null) {
    if (!newSource) return;
    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('fieldset input[type="radio"]')
    );

    const userLocalRadio = preserveLocalValue
      ? /** @type {HTMLInputElement | null} */ (
          Array.from(radios).find(
            (r) => !r.dataset.connectedProductUrl && r.value === preserveLocalValue
          )
        )
      : Array.from(radios).find(
          (r) => !r.dataset.connectedProductUrl && r.dataset.currentChecked === 'true'
        );

    // Only preserve the local option if it's available in the new server HTML.
    const serverUserLocalRadio = userLocalRadio?.dataset.optionValueId
      ? /** @type {HTMLElement | null} */ (newSource.querySelector(`input[data-option-value-id="${userLocalRadio.dataset.optionValueId}"]`))
      : null;
    const shouldPreserveLocal =
      !!userLocalRadio && serverUserLocalRadio?.dataset.optionAvailable === 'true';

    for (const radio of radios) {
      const optionValueId = radio.dataset.optionValueId;
      if (!optionValueId) continue;

      if (shouldPreserveLocal && !radio.dataset.connectedProductUrl) {
        const isUserChoice = radio === userLocalRadio;
        radio.checked = preserveLocalValue ? isUserChoice : radio.dataset.currentChecked === 'true';
        radio.dataset.currentChecked = radio.checked ? 'true' : 'false';
        continue;
      }

      const serverRadio = newSource.querySelector(`input[data-option-value-id="${optionValueId}"]`);
      const isChecked = serverRadio?.hasAttribute('checked') ?? false;

      radio.checked = isChecked;
      radio.dataset.currentChecked = isChecked ? 'true' : 'false';
    }

    // Rebuild #checkedIndices to match the newly synced DOM state so that the next
    // updateSelectedOption call uses the correct current index for animation tracking.
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const fieldsetRadios = this.#radios[fieldsetIndex];
      if (!fieldsetRadios) return;
      const checkedIndex = fieldsetRadios.findIndex((radio) => radio.checked);
      this.#checkedIndices[fieldsetIndex] = checkedIndex === -1 ? [] : [checkedIndex];
    });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });

    // The product changed, so the option set may differ — rebuild the stale radio bookkeeping.
    if (newProduct) this.#resyncRadioState();
    // Apply the server's selection (morph preserves the stale data-current-checked and dirty radios mislead it).
    this.#syncCheckedState(newVariantPickerSource);

    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    const currentLocalRadio = /** @type {HTMLInputElement | null} */ (
      this.querySelector('fieldset input[type="radio"][data-current-checked="true"][data-connected-product-url=""]')
    );
    const userLocalValue = currentLocalRadio?.value ?? null;

    morph(main, newMain);

    // updateMain only runs when a combined-listing option loads a different product, so the picker's option set
    // may differ from the one captured in connectedCallback — rebuild the stale radio bookkeeping.
    this.#resyncRadioState();

    // Apply the server's selection from the new picker (its `checked` attributes stay pristine through morph).
    const newPicker =
      newMain.querySelector(`${this.tagName.toLowerCase()}[data-block-id="${this.dataset.blockId}"]`) ??
      newMain.querySelector(this.tagName.toLowerCase());
    this.#syncCheckedState(newPicker, this.#pendingLocalOptionRefetch ? null : userLocalValue);

    if (this.#pendingLocalOptionRefetch) {
      this.#pendingLocalOptionRefetch = false;
      const serverVariantJson = this.querySelector('form script[type="application/json"]');
      const renderedVariantId = serverVariantJson ? JSON.parse(serverVariantJson.textContent || '{}').id?.toString() : null;
      if (renderedVariantId) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', renderedVariantId);
        history.replaceState({}, '', url.toString());
        const variantInput = /** @type {HTMLInputElement | null} */ (document.querySelector('input[ref="variantId"][name="id"]'));
        if (variantInput) variantInput.value = renderedVariantId;
      }
    } else if (userLocalValue) {
      const targetLocalRadio = /** @type {HTMLInputElement | null} */ (
        this.querySelector(`fieldset input[type="radio"][data-connected-product-url=""][data-option-available="true"][value="${CSS.escape(userLocalValue)}"]`)
      );
      const serverVariantJson = this.querySelector('form script[type="application/json"]');
      const serverVariantId = serverVariantJson ? JSON.parse(serverVariantJson.textContent || '{}').id?.toString() : null;
      if (targetLocalRadio?.dataset.variantId && targetLocalRadio.dataset.variantId !== serverVariantId) {
        const base = (this.dataset.productUrl || window.location.pathname).split('?')[0];
        const view = getViewParameterValue();
        this.#pendingLocalOptionRefetch = true;
        const buyButtonsBlock = document.querySelector('.buy-buttons-block');
        if (buyButtonsBlock instanceof HTMLElement) buyButtonsBlock.style.visibility = 'hidden';
        this.fetchUpdatedSection(
          `${base}?${view ? `view=${view}&` : ''}variant=${targetLocalRadio.dataset.variantId}`,
          'main'
        );
        return;
      }
    }

    yieldToMainThread().then(() => {
      for (const staleButton of document.querySelectorAll('[data-native-pre-order-btn-added]')) {
        staleButton.remove();
      }
      window.dispatchEvent(new Event('locationchange'));
      window.PPretry?.();
    });
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
    return selectedOptions.map((option) => {
      const el = /** @type {HTMLInputElement} */ (option);
      const { optionValueId } = el.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
